import { MigrationDefinition, MigrationsDocument, StoredMigration } from '../types/migration.types';

/**
 * Konvertiert eine Funktion zu einem String für DB-Speicherung
 */
function functionToString(fn: (doc: any) => any): string {
  return fn.toString();
}

/**
 * Konvertiert MigrationDefinitions zu StoredMigrations (Strings für DB)
 */
export function toStoredMigrations(migrations: MigrationDefinition[]): Record<string, StoredMigration> {
  const stored: Record<string, StoredMigration> = {};
  for (const m of migrations) {
    const key = `${m.fromVersion}->${m.toVersion}`;
    stored[key] = {
      upgrade: functionToString(m.upgrade),
      downgrade: functionToString(m.downgrade),
      dataLossWarnings: m.dataLossWarnings || [],
      addedInVersion: m.toVersion
    };
  }
  return stored;
}

/**
 * Erstellt oder aktualisiert das _design/migrations Dokument in CouchDB.
 * Fügt nur neue Migrationen hinzu, überschreibt keine bestehenden.
 *
 * @param db PouchDB/CouchDB Instanz
 * @param currentVersion Aktuelle Dokumentversion der App
 * @param storedMigrations Migrationen als StoredMigration-Objekte
 * @returns true wenn Änderungen vorgenommen wurden
 */
export async function seedMigrationsDocument(
  db: any,
  currentVersion: number,
  storedMigrations: Record<string, StoredMigration>
): Promise<boolean> {
  let doc: MigrationsDocument;
  let isNew = false;

  try {
    doc = await db.get('_design/migrations') as MigrationsDocument;

    // Früher Abbruch: Wenn DB bereits aktuell oder neuer, nichts tun
    if (doc.latestVersion >= currentVersion) {
      console.log(`DB-Migrationen sind bereits aktuell (DB: ${doc.latestVersion}, App: ${currentVersion})`);
      return false;
    }
  } catch (err: any) {
    if (err.status === 404) {
      // Dokument existiert nicht, neu anlegen
      doc = {
        _id: '_design/migrations',
        migrations: {},
        latestVersion: -1,
        updatedAt: new Date().toISOString()
      };
      isNew = true;
    } else {
      throw err;
    }
  }

  // Neue Migrationen hinzufügen (bestehende nicht überschreiben)
  let updated = isNew;

  for (const [key, migration] of Object.entries(storedMigrations)) {
    if (!doc.migrations[key]) {
      doc.migrations[key] = migration;
      updated = true;
      console.log(`Migration ${key} hinzugefügt`);
    }
  }

  // latestVersion aktualisieren
  if (currentVersion > doc.latestVersion) {
    doc.latestVersion = currentVersion;
    updated = true;
  }

  if (updated) {
    doc.updatedAt = new Date().toISOString();
    await db.put(doc);
    console.log(`_design/migrations ${isNew ? 'erstellt' : 'aktualisiert'} (latestVersion: ${doc.latestVersion})`);
  } else {
    console.log('_design/migrations ist bereits aktuell');
  }

  return updated;
}

/**
 * Löscht das _design/migrations Dokument (für Tests)
 */
export async function deleteMigrationsDocument(db: any): Promise<void> {
  try {
    const doc = await db.get('_design/migrations');
    await db.remove(doc);
    console.log('_design/migrations gelöscht');
  } catch (err: any) {
    if (err.status !== 404) {
      throw err;
    }
  }
}

/**
 * Gibt das aktuelle _design/migrations Dokument zurück (für Debugging)
 */
export async function getMigrationsDocument(db: any): Promise<MigrationsDocument | null> {
  try {
    return await db.get('_design/migrations') as MigrationsDocument;
  } catch (err: any) {
    if (err.status === 404) {
      return null;
    }
    throw err;
  }
}
